import { useEffect, useMemo, useRef, useState, useContext } from 'react';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import axios from '../../../axios';
import { Paper, Text, Button } from '@mantine/core';
import AuthContext from '../../../AuthContext';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

const Example = ({ year, centre, generateClicked }) => {
  const { userType } = useContext(AuthContext);
  const [paid, setPaid] = useState('');
  const [balance, setBalance] = useState('');
  const [expense, setExpense] = useState('');
  const navigate = useNavigate();
  const rowVirtualizerInstanceRef = useRef(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10, // Default page size
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatCurrency = (value) => {
    if (isNaN(value)) return value; // In case value is not a number, return it as-is
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(value);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'centre_name',
        header: 'Centre Name',
        size: 200,
        visible: true,
      },
      {
        accessorKey: 'head',
        header: 'Head',
        size: 150,
        visible: true,
      },
      {
        accessorKey: 'tot_paid',
        header: 'Total Paid',
        size: 150,
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
        visible: true,
      },
      {
        accessorKey: 'tot_expenses',
        header: 'Total Expenses',
        size: 150,
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
        visible: true,
      },
      {
        accessorKey: 'latest_balance',
        header: 'Total Balance',
        size: 150,
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
        visible: true,
      },
    ],
    []
  );

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sorting, setSorting] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState(columns);

  useEffect(() => {
    setIsLoading(true);
    axios
      .post('/ledger/ledgerReport', {
        financial_year: year,
        centre_id: centre,
      })
      .then((res) => {
        setData(res.data.data);
        console.log(res.data);
        setPaid(res.data.totals.total_paid);
        setExpense(res.data.totals.total_expenses);
        setBalance(res.data.totals.total_balance);
        setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
      });
  }, [year, centre, generateClicked]);

  useEffect(() => {
    try {
      rowVirtualizerInstanceRef.current?.scrollToIndex(0);
    } catch (e) {
      console.log(e);
    }
  }, [sorting]);

  const table = useMantineReactTable({
    data,
    columns: visibleColumns,
    enableGlobalFilter: true,
    enableColumnFilterModes: true,
    enablePinning: true,
    enablePagination: true,
    enableMultiSort: true,
    columnFilterDisplayMode: 'popover',
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    enableColumnResizing: true,
    enableColumnFilter: true,
    enableColumnSelection: true,
    enableGlobalFilterModes: true,
    enableStickyHeader: true,
    enableColumnSticky: true,
    enableRowNumbers: true,
    enableRowActions: true,
    state: { pagination, sorting, isLoading },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
  });

  const handleExport = () => {
    // Temporarily set pageSize to include all filtered rows
    table.setPageSize(data.length || 1000);

    // Get filtered rows
    const filteredData = table.getFilteredRowModel().rows.map(({ original }) => ({
      'Centre Name': original.centre_name,
      'Head': original.head,
      'Total Paid': original.tot_paid,
      'Total Expenses': original.tot_expenses,
      'Total Balance': original.latest_balance,
    }));

    // Restore original page size
    table.setPageSize(pagination.pageSize);

    if (filteredData.length === 0) {
      alert('No data to export! Please apply a valid filter or check the data.');
      return;
    }

    // Add a totals row
    filteredData.push({
      'Centre Name': 'Totals',
      'Head': '',
      'Total Paid': paid,
      'Total Expenses': expense,
      'Total Balance': balance,
    });

    // Convert JSON data to a worksheet
    const worksheet = XLSX.utils.json_to_sheet(filteredData);

    // Adjust column headers
    const headers = ['Centre Name', 'Head', 'Total Paid', 'Total Expenses', 'Total Balance'];
    XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: 'A1' });

    // Create a workbook and append the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Filtered Ledger Report');

    // Generate Excel file and trigger download
    XLSX.writeFile(workbook, `Ledger_Report_${year}.xlsx`);
  };

  return (
    <Paper shadow="sm" p="lg" withBorder>
      <div style={{ width: '100%' }}>
        <Button onClick={handleExport} color="green" style={{ marginBottom: '16px' }}>
          Export to Excel
        </Button>
        {/* Display the total paid, expenses, and balance amounts */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <Text size="md" weight={500} mb="md" mt="0">
            <span className="text-green-600">
              Total Paid
              <hr />
              <p className="text-2xl">{formatCurrency(paid)}</p>
            </span>
          </Text>
          <Text size="md" weight={500} mb="md" mt="0">
            <span className="text-blue-600">
              Total Expenses
              <hr />
              <p className="text-2xl">{formatCurrency(expense)}</p>
            </span>
          </Text>
          <Text size="md" weight={500} mb="md" mt="0">
            <span className="text-red-600">
              Total Balance
              <hr />
              <p className="text-2xl">{formatCurrency(balance)}</p>
            </span>
          </Text>
        </div>
      </div>

      <div style={{ width: '100%' }}>
        <MantineReactTable
          table={table}
          striped
          highlightOnHover
          horizontalSpacing="md"
          fontSize="sm"
          withColumnBorders
          borderRadius="md"
          withGlobalFilter
          loading={isLoading}
          enableRowSelection={true}
          enableColumnSelection={true}
          height={400}
          rowVirtualizerInstanceRef={rowVirtualizerInstanceRef}
          enablePagination={true}
        />
      </div>
    </Paper>
  );
};

export default Example;